import { Card, Radio, Select, Tag } from "antd";
import dayjs from "dayjs";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import DatePicker from "shared/components/Form/DatePicker";
import Input from "shared/components/Input";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  INSURANCE_PLAN_RISKS,
  PRICER_TYPES,
  PRICING_MODES,
  STATUS_PUBLICATION,
  TAG_COLORS,
} from "utils/constants";
import { bindInputProps } from "utils/helpers/input";
import AgeBracketManager from "../Components/AgeBracketManager";
import ConditionsManager from "../Components/ConditionsManager";
import DocumentTemplatesManager from "../Components/DocumentTemplatesManager";
import RegimeManager from "../Components/RegimeManager";
import ZoneManager from "../Components/ZoneManager";

const PlanDetails = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  data,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const [updatePlan] = useMutation(graphql.mutations.UPDATE_INSURANCE_PLAN);
  const planId = data.id;

  const pricingModeOptions = [
    {
      value: PRICING_MODES.PACKAGED,
      label: t("CONFIGURE.PLAN_DETAILS.PRICING_MODE_PACKAGED"),
      description: t("CONFIGURE.PLAN_DETAILS.PRICING_MODE_PACKAGED_DESC"),
    },
    {
      value: PRICING_MODES.COMPOSABLE,
      label: t("CONFIGURE.PLAN_DETAILS.PRICING_MODE_COMPOSABLE"),
      description: t("CONFIGURE.PLAN_DETAILS.PRICING_MODE_COMPOSABLE_DESC"),
    },
  ];

  const riskOptions = [
    {
      value: INSURANCE_PLAN_RISKS.HEALTH,
      label: t("CONFIGURE.PLAN_DETAILS.RISK_HEALTH"),
    },
    {
      value: INSURANCE_PLAN_RISKS.TERMINAL,
      label: t("CONFIGURE.PLAN_DETAILS.RISK_TERMINAL"),
    },
    {
      value: INSURANCE_PLAN_RISKS.PRO,
      label: t("CONFIGURE.PLAN_DETAILS.RISK_PRO"),
    },
    {
      value: INSURANCE_PLAN_RISKS.PROVIDENT,
      label: t("CONFIGURE.PLAN_DETAILS.RISK_PROVIDENT"),
    },
    {
      value: INSURANCE_PLAN_RISKS.SPORTS_LEISURE,
      label: t("CONFIGURE.PLAN_DETAILS.RISK_SPORTS_LEISURE"),
    },
  ];

  const statusOptions = [
    {
      value: STATUS_PUBLICATION.ACTIVE,
      label: t("CONFIGURE.PLAN_DETAILS.STATUS_ACTIVE"),
    },
    {
      value: STATUS_PUBLICATION.DRAFT,
      label: t("CONFIGURE.PLAN_DETAILS.STATUS_DRAFT"),
    },
    {
      value: STATUS_PUBLICATION.INACTIVE,
      label: t("CONFIGURE.PLAN_DETAILS.STATUS_INACTIVE"),
    },
    {
      value: STATUS_PUBLICATION.RETIRED,
      label: t("CONFIGURE.PLAN_DETAILS.STATUS_RETIRED"),
    },
  ];

  const zoneTypes = [
    {
      value: "DEPARTMENT",
      label: t("CONFIGURE.PLAN_DETAILS.ZONE_DEPARTMENT"),
    },
    { value: "ZIP_CODE", label: t("CONFIGURE.PLAN_DETAILS.ZONE_ZIP") },
    { value: "FREE_FORM", label: t("CONFIGURE.PLAN_DETAILS.ZONE_FREE") },
  ];

  const saveField = (field, value) => {
    updatePlan({
      variables: {
        where: { id: planId },
        data: { [field]: value },
      },
    });
  };

  const handleBlurSave = (field) => {
    saveField(field, get(values, field));
  };

  const handleDirectSave = (field, value) => {
    setFieldValue(field, value);
    saveField(field, value);
  };

  return (
    <div className="configure-step">
      <Card
        title={t("CONFIGURE.PLAN_DETAILS.GENERAL_INFO")}
        className="m-bottom-16"
      >
        <div className="grid--2">
          <Input
            {...bindInputProps({
              name: "name",
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
            })}
            type="simple"
            label={t("CONFIGURE.PLAN_DETAILS.NAME")}
            placeholder={t("CONFIGURE.PLAN_DETAILS.NAME_PLACEHOLDER")}
            required
            onBlur={() => handleBlurSave("name")}
          />
          <Input
            {...bindInputProps({
              name: "identifier",
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
            })}
            type="simple"
            label={t("CONFIGURE.PLAN_DETAILS.IDENTIFIER")}
            placeholder={t("CONFIGURE.PLAN_DETAILS.IDENTIFIER_PLACEHOLDER")}
            required
            onBlur={() => handleBlurSave("identifier")}
          />
        </div>
        <div className="m-top-16">
          <Input
            {...bindInputProps({
              name: "description",
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
            })}
            type="textarea"
            label={t("CONFIGURE.PLAN_DETAILS.DESCRIPTION")}
            placeholder={t("CONFIGURE.PLAN_DETAILS.DESCRIPTION_PLACEHOLDER")}
            onBlur={() => handleBlurSave("description")}
          />
        </div>
        <div className="grid--2 m-top-16">
          <div>
            <label className="input__label">
              {t("CONFIGURE.PLAN_DETAILS.RISK_TYPE")}
            </label>
            <Radio.Group
              value={get(values, "risk")}
              onChange={(e) => handleDirectSave("risk", e.target.value)}
            >
              {riskOptions.map((opt) => (
                <Radio.Button key={opt.value} value={opt.value}>
                  {opt.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
          <div>
            <label className="input__label">
              {t("CONFIGURE.PLAN_DETAILS.STATUS")}
            </label>
            <Radio.Group
              value={get(values, "status")}
              onChange={(e) => handleDirectSave("status", e.target.value)}
            >
              {statusOptions.map((opt) => (
                <Radio.Button key={opt.value} value={opt.value}>
                  {opt.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
        </div>
        <div className="m-top-16">
          <label className="input__label">
            {t("CONFIGURE.PLAN_DETAILS.PERIOD")}
          </label>
          <DatePicker
            name="date"
            placeholder="Select period"
            placeholders={["Date de début", "Date de fin"]}
            defaultValue={
              get(values, "startDate") &&
              get(values, "endDate") && [
                dayjs(get(values, "startDate")),
                dayjs(get(values, "endDate")),
              ]
            }
            onChange={(_, dates) => {
              setFieldValue("startDate", dates[0] || null);
              setFieldValue("endDate", dates[1] || null);
              updatePlan({
                variables: {
                  where: { id: planId },
                  data: {
                    startDate: dates[0] || null,
                    endDate: dates[1] || null,
                  },
                },
              });
            }}
          />
        </div>
      </Card>

      <Card
        title={t("CONFIGURE.PLAN_DETAILS.PRICING_MODE")}
        className="m-bottom-16"
      >
        <p className="m-bottom-16 configure-step__muted">
          {t("CONFIGURE.PLAN_DETAILS.PRICING_MODE_INTRO")}
        </p>
        <div className="d-flex gap-16">
          {pricingModeOptions.map((mode) => {
            const currentMode =
              get(values, "config.pricing.mode") === PRICER_TYPES.COMPOSABLE ||
              get(values, "pricer.type") === PRICER_TYPES.COMPOSABLE
                ? PRICING_MODES.COMPOSABLE
                : PRICING_MODES.PACKAGED;
            const isSelected = currentMode === mode.value;
            return (
              <div
                key={mode.value}
                className={`pricing-mode-card pricing-mode-card__inner${isSelected ? " pricing-mode-card--selected" : ""}`}
              >
                <div className="d-flex justify--between">
                  <h4>{mode.label}</h4>
                  {isSelected && (
                    <Tag color={TAG_COLORS.BLUE}>
                      {t("CONFIGURE.TAGS.SELECTED")}
                    </Tag>
                  )}
                </div>
                <p>{mode.description}</p>
              </div>
            );
          })}
        </div>
      </Card>

      <ConditionsManager
        params={get(values, "params") || []}
        planId={data.id}
        setFieldValue={setFieldValue}
      />

      <Card title={t("CONFIGURE.PLAN_DETAILS.ZONES")} className="m-bottom-16">
        <div className="m-bottom-16">
          <label className="input__label">
            {t("CONFIGURE.PLAN_DETAILS.ZONE_TYPE")}
          </label>
          <Select
            value={get(values, "zoneType")}
            onChange={(val) => handleDirectSave("zoneType", val)}
            className="configure-field--zone"
            placeholder={t("CONFIGURE.PLAN_DETAILS.ZONE_TYPE_PLACEHOLDER")}
            allowClear
          >
            {zoneTypes.map((zoneType) => (
              <Select.Option key={zoneType.value} value={zoneType.value}>
                {zoneType.label}
              </Select.Option>
            ))}
          </Select>
        </div>
        <ZoneManager
          zones={get(values, "zones") || []}
          zoneType={get(values, "zoneType")}
          planId={planId}
          setFieldValue={setFieldValue}
        />
      </Card>

      <div className="m-top-16">
        <AgeBracketManager
          ageBrackets={get(values, "ageBrackets") || []}
          planId={planId}
          setFieldValue={setFieldValue}
        />
      </div>

      <div className="m-top-16">
        <RegimeManager
          regimes={get(values, "regimes") || []}
          planId={planId}
          setFieldValue={setFieldValue}
        />
      </div>

      <div className="m-top-16">
        <DocumentTemplatesManager
          documentTemplates={get(values, "documentTemplates") || []}
          planId={planId}
          setFieldValue={setFieldValue}
        />
      </div>
    </div>
  );
};

export default PlanDetails;
